import React, { useMemo, useState } from "react"

import {
  FILTER_OPERATOR,
  TTextOperator,
  TableVisualizationColumnFilter,
} from "~/ui-rtk/constants/table-visualization-types"
import { Select } from "~/ui-rtk/components/ui/controls"
import Input from "~/ui-rtk/components/ui/controls/Input"
import { Divider } from "~/ui-rtk/components/ui/common"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import { CheckMarkSvg } from "~/ui-rtk/components/ui/svg/essentials"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { type TFilterColumnConfig } from "../types"

const MAX_SUGGESTIONS = 20

type SearchableSelectFilterProps = {
  column: TFilterColumnConfig
  onChange: (key: string, value: string[], operator: TTextOperator) => void
  defaultFilter?: TableVisualizationColumnFilter
  getOptions?: (columnKey: string) => TMultiSelectOption[]
  isLoadingOptions?: boolean
}

const SearchableSelectFilter: React.FC<SearchableSelectFilterProps> = ({
  column: columnDef,
  onChange,
  defaultFilter,
  getOptions,
  isLoadingOptions,
}) => {
  // Derived from the filter's column config so the placeholder and empty state
  // read naturally for any searchable-select filter, not just session mediums.
  const optionsLabel = (columnDef.header || columnDef.key).toLowerCase()

  const textOperatorOptions = useMemo(
    () =>
      Object.keys(FILTER_OPERATOR).map(key => ({
        value: key,
        label: FILTER_OPERATOR[key as TTextOperator],
      })),
    [],
  )

  const options = useMemo(() => {
    if (getOptions) {
      const optionsKey = columnDef.filterKey || columnDef.key
      return getOptions(optionsKey)
    }
    return []
  }, [getOptions, columnDef.key, columnDef.filterKey])

  const [search, setSearch] = useState<string>("")
  const [selected, setSelected] = useState<string[]>(
    (defaultFilter?.value as string[]) ?? [],
  )
  const [operator, setOperator] = useState<TTextOperator>(
    defaultFilter?.operator ?? "contains",
  )

  // Options already arrive ranked by session volume (desc). Narrow to the typed
  // substring and cap the suggestion list so the dropdown stays manageable.
  const suggestions = useMemo(() => {
    const query = search.trim().toLowerCase()
    const matches = query
      ? options.filter(
          option =>
            option.label.toLowerCase().includes(query) ||
            option.value.toLowerCase().includes(query),
        )
      : options
    return matches.slice(0, MAX_SUGGESTIONS)
  }, [options, search])

  const emitChange = (values: string[], nextOperator: TTextOperator) => {
    onChange(columnDef.key, values, nextOperator)
  }

  const handleSelectOperator = (value: TTextOperator) => {
    setOperator(value)
    if (selected.length > 0) {
      emitChange(selected, value)
    }
  }

  const toggleValue = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value]
    setSelected(next)
    emitChange(next, operator)
  }

  const handleRemove = (value: string) => {
    const next = selected.filter(v => v !== value)
    setSelected(next)
    emitChange(next, operator)
  }

  return (
    <div className="space-y-3">
      <label className="ml-1 font-semibold">
        {columnDef.header || columnDef.key}
      </label>
      <Select
        defaultValue={operator}
        options={textOperatorOptions}
        className="bg-basic-blue"
        onChange={$ev =>
          handleSelectOperator(
            ($ev.target as HTMLSelectElement).value as TTextOperator,
          )
        }
      />
      <Divider />
      <Input
        value={search}
        fullWidth
        className="bg-basic-blue"
        placeholder={`Search ${optionsLabel}...`}
        onChange={e => setSearch(e.target.value)}
      />
      <div className="max-h-50 overflow-y-auto scrollbar container w-full text-white border rounded-md border-basic-blue bg-basic-blue">
        {suggestions.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-basic-grey-inactive">
            {isLoadingOptions ? "Loading..." : "No matches"}
          </div>
        ) : (
          suggestions.map(option => {
            const isSelected = selected.includes(option.value)
            return (
              <div
                key={option.value}
                className={cn(
                  "px-2 py-1.5 text-sm cursor-pointer flex items-center space-x-2 w-full hover:bg-background-dark-shade",
                )}
                onClick={() => toggleValue(option.value)}
                aria-label={option.label}
              >
                {isSelected && <CheckMarkSvg className="me-1" fill="green" />}
                <span className={cn(!isSelected && "ms-6 pl-1")}>
                  {option.label}
                </span>
              </div>
            )
          })
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap mt-2 gap-y-1.5">
          {selected.map(value => {
            const option = options.find(o => o.value === value)
            return (
              <Chip
                className="border border-basic-pink px-2 py-1.5 mr-2 leading-4"
                key={`chip-${value}`}
                onRemove={() => handleRemove(value)}
              >
                <span className="text-sm">{option?.label ?? value}</span>
              </Chip>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default SearchableSelectFilter
